import { useRef, useState, useCallback } from 'react';
import { AlertTriangle, Image as ImageIcon, Palette, Settings2, Sparkles, Square, Upload, X } from 'lucide-react';
import { useQR, QR_PRESETS, METALLIC_COLORS } from '@/hooks/useQRContext';
import { useToast } from '@/components/common/Toast';
import AdvancedColorPicker from '@/components/common/AdvancedColorPicker';
import Select from '@/components/common/Select';
import Slider from '@/components/common/Slider';
import { cn, readFileAsDataURL, validateImageFile } from '@/utils/helpers';

const DOT_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dots', label: 'Dots' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
];

const CORNER_SQUARE_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
];

const CORNER_DOT_STYLES = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
];

const ERROR_CORRECTION_LEVELS = [
  { value: 'L', label: 'Low (7%)' },
  { value: 'M', label: 'Medium (15%)' },
  { value: 'Q', label: 'Quartile (25%)' },
  { value: 'H', label: 'High (30%)' },
];

const SHAPE_OPTIONS = [
  { value: 'square', label: 'Square' },
  { value: 'circle', label: 'Circle' },
];

const STYLE_TABS = [
  { id: 'presets', label: 'Presets', icon: Sparkles },
  { id: 'colors', label: 'Colors', icon: Palette },
  { id: 'style', label: 'Style', icon: Square },
  { id: 'logo', label: 'Logo', icon: ImageIcon },
  { id: 'settings', label: 'Settings', icon: Settings2 },
];

function getContrastRatio(color1, color2) {
  const getLuminance = (hex) => {
    const normalized = hex?.replace('#', '') || '000000';
    const rgb = parseInt(normalized, 16);
    const r = ((rgb >> 16) & 255) / 255;
    const g = ((rgb >> 8) & 255) / 255;
    const b = (rgb & 255) / 255;
    const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  };

  try {
    const l1 = getLuminance(color1);
    const l2 = getLuminance(color2);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  } catch {
    return 21;
  }
}

function QRCustomizer() {
  const { qrConfig, updateConfig, updateConfigBatch, applyPreset } = useQR();
  const { success, error } = useToast();
  const [activeTab, setActiveTab] = useState('presets');
  const fileInputRef = useRef(null);

  const backgroundForContrast = qrConfig.transparentBackground ? '#ffffff' : (qrConfig.backgroundColor || '#ffffff');
  const hasContrastWarning = getContrastRatio(qrConfig.dotsColor || '#000000', backgroundForContrast) < 4;

  const handleLogoUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      error(validation.error);
      return;
    }

    try {
      const dataUrl = await readFileAsDataURL(file);
      updateConfigBatch({ image: dataUrl, errorCorrectionLevel: 'H' });
      success('Logo uploaded. Error correction was set to High.');
    } catch {
      error('Failed to upload logo');
    }
  }, [error, success, updateConfigBatch]);

  const handleRemoveLogo = useCallback(() => {
    updateConfig('image', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [updateConfig]);

  const renderPresets = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Quick-start templates with scan-friendly defaults.
      </p>
      <div className="grid grid-cols-2 gap-3">
        {Object.entries(QR_PRESETS).map(([key, preset]) => (
          <button
            key={key}
            onClick={() => applyPreset(key)}
            className={cn(
              'p-4 rounded-xl border-2 text-left transition-all hover:border-primary-500 hover:shadow-md',
              qrConfig.dotsType === preset.config.dotsType && qrConfig.dotsColor === preset.config.dotsColor
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700'
            )}
          >
            <div
              className="w-10 h-10 rounded-lg mb-2 border border-gray-100"
              style={{ backgroundColor: preset.config.dotsColor }}
            />
            <p className="font-medium text-gray-900 dark:text-gray-100">{preset.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{preset.description}</p>
          </button>
        ))}
      </div>
    </div>
  );

  const renderColors = () => (
    <div className="space-y-6">
      {hasContrastWarning && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Low contrast may make this QR code harder to scan. Use a darker foreground or lighter background.
          </p>
        </div>
      )}

      <AdvancedColorPicker
        label="QR Code Color"
        value={qrConfig.dotsColor}
        onChange={(color) => updateConfig('dotsColor', color)}
        gradient={qrConfig.dotsGradient}
        onGradientChange={(gradient) => updateConfig('dotsGradient', gradient)}
        showContrast
        contrastWith={backgroundForContrast}
      />

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={qrConfig.transparentBackground || false}
          onChange={(event) => updateConfig('transparentBackground', event.target.checked)}
          className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="font-medium text-gray-700 dark:text-gray-300">Transparent Background</span>
      </label>

      {!qrConfig.transparentBackground && (
        <AdvancedColorPicker
          label="Background Color"
          value={qrConfig.backgroundColor}
          onChange={(color) => updateConfig('backgroundColor', color)}
          gradient={qrConfig.backgroundGradient}
          onGradientChange={(gradient) => updateConfig('backgroundGradient', gradient)}
        />
      )}

      <AdvancedColorPicker
        label="Corner Squares Color"
        value={qrConfig.cornersSquareColor}
        onChange={(color) => updateConfig('cornersSquareColor', color)}
        gradient={qrConfig.cornersSquareGradient}
        onGradientChange={(gradient) => updateConfig('cornersSquareGradient', gradient)}
      />

      <AdvancedColorPicker
        label="Corner Dots Color"
        value={qrConfig.cornersDotColor}
        onChange={(color) => updateConfig('cornersDotColor', color)}
        gradient={qrConfig.cornersDotGradient}
        onGradientChange={(gradient) => updateConfig('cornersDotGradient', gradient)}
      />

      <div className="grid grid-cols-3 gap-2">
        {Object.entries(METALLIC_COLORS).map(([key, metallic]) => (
          <button
            key={key}
            onClick={() => updateConfigBatch({
              dotsGradient: metallic,
              cornersSquareGradient: metallic,
              cornersDotGradient: metallic,
            })}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 transition-all"
          >
            <div
              className="w-full h-6 rounded"
              style={{ background: `linear-gradient(${metallic.rotation}deg, ${metallic.colors.map(c => `${c.color} ${c.offset * 100}%`).join(', ')})` }}
            />
            <span className="text-xs text-gray-600 dark:text-gray-400 mt-1 block">{metallic.name}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStyle = () => (
    <div className="space-y-6">
      <Select label="Dot Style" value={qrConfig.dotsType} onChange={(value) => updateConfig('dotsType', value)} options={DOT_STYLES} />
      <Select label="Corner Square Style" value={qrConfig.cornersSquareType} onChange={(value) => updateConfig('cornersSquareType', value)} options={CORNER_SQUARE_STYLES} />
      <Select label="Corner Dot Style" value={qrConfig.cornersDotType} onChange={(value) => updateConfig('cornersDotType', value)} options={CORNER_DOT_STYLES} />
      <Select label="QR Code Shape" value={qrConfig.shapeType || 'square'} onChange={(value) => updateConfig('shapeType', value)} options={SHAPE_OPTIONS} />
    </div>
  );

  const renderLogo = () => (
    <div className="space-y-6">
      {qrConfig.image ? (
        <div className="relative w-24 h-24">
          <img src={qrConfig.image} alt="Logo preview" className="w-full h-full object-contain rounded-lg border border-gray-200 dark:border-gray-700" />
          <button onClick={handleRemoveLogo} className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors" aria-label="Remove logo">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-primary-500 hover:text-primary-500 transition-all flex flex-col items-center gap-2"
        >
          <Upload className="w-8 h-8" />
          <span className="font-medium">Click to upload logo</span>
          <span className="text-xs">PNG, JPG, SVG up to 2MB</span>
        </button>
      )}
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />

      {qrConfig.image && (
        <>
          <Slider label="Logo Size" value={Math.round(qrConfig.imageSize * 100)} onChange={(value) => updateConfig('imageSize', value / 100)} min={10} max={40} unit="%" />
          <Slider label="Logo Margin" value={qrConfig.imageMargin} onChange={(value) => updateConfig('imageMargin', value)} min={0} max={20} unit="px" />
        </>
      )}
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <Select label="Error Correction Level" value={qrConfig.errorCorrectionLevel} onChange={(value) => updateConfig('errorCorrectionLevel', value)} options={ERROR_CORRECTION_LEVELS} />
      <Slider label="QR Code Size" value={qrConfig.width} onChange={(value) => updateConfigBatch({ width: value, height: value })} min={200} max={500} step={10} unit="px" />
      <Slider label="Margin (Quiet Zone)" value={qrConfig.margin} onChange={(value) => updateConfig('margin', value)} min={0} max={50} unit="px" />
      <p className="text-sm text-gray-500 dark:text-gray-400">
        A margin of at least 10px is recommended for reliable scanning.
      </p>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'presets': return renderPresets();
      case 'colors': return renderColors();
      case 'style': return renderStyle();
      case 'logo': return renderLogo();
      case 'settings': return renderSettings();
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex overflow-x-auto gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
        {STYLE_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap text-sm font-medium transition-all duration-200',
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            )}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>
      {renderTabContent()}
    </div>
  );
}

export default QRCustomizer;
